import React, { useState } from 'react';
import { X, CheckCircle, XCircle, DollarSign } from 'lucide-react';

const StatusUpdateModal = ({ invoice, isOpen, onClose, onApprove, onReject, onMarkPaid }) => {
    const [notes, setNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentReference, setPaymentReference] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (action) => {
        if (action === 'approve') {
            setLoading(true);
            try {
                await onApprove(invoice, notes);
                onClose();
            } catch (error) {
                console.error('Error approving invoice:', error);
            } finally {
                setLoading(false);
            }
        } else if (action === 'reject') {
            if (!rejectionReason.trim()) {
                setFieldError('Rejection reason is required.');
                return;
            }
            setFieldError('');
            setLoading(true);
            try {
                await onReject(invoice, rejectionReason.trim(), notes);
                onClose();
            } catch (error) {
                console.error('Error rejecting invoice:', error);
            } finally {
                setLoading(false);
            }
        } else if (action === 'paid') {
            if (!paymentMethod.trim()) {
                setFieldError('Payment method is required.');
                return;
            }
            if (!paymentReference.trim()) {
                setFieldError('Payment reference is required.');
                return;
            }
            setFieldError(''); 
            setLoading(true);
            try {
                await onMarkPaid(invoice, paymentMethod, paymentReference, notes);
                onClose();
            } catch (error) {
                console.error('Error marking invoice as paid:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const resetForm = () => {
        setNotes('');
        setRejectionReason('');
        setPaymentMethod('');
        setPaymentReference('');
        setFieldError('');
        setLoading(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-drk-900 flex items-center justify-center z-50 mt0">
            <div className="bg-white rounded-lg w-full max-w-md mx-4">
                <div className='border-b px-6 pt-4 pb-4'>
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {invoice.status === 'submitted' ? 'Update Invoice Status' : 'Mark Invoice as Paid'}
                    </h3>
                    <button
                        onClick={handleClose}
                        className="bg-red-500 text-white hover:text-white p-1 rounded"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                </div>
                    <div className='p-6'>
                <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                        Invoice: <span className="font-medium">{invoice.invoice_number}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                        Current Status: <span className="font-medium capitalize">{invoice.status}</span>
                    </p>
                </div>

                {invoice.status === 'submitted' && (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add any notes about this status update..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows="3"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rejection reason (required to reject)
                            </label>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => {
                                    setRejectionReason(e.target.value);
                                    setFieldError('');
                                }}
                                placeholder="Please provide a reason for rejection..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                rows="3"
                            />
                        </div>
                        {fieldError && <p className="mb-3 text-sm text-red-600">{fieldError}</p>}
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleSubmit('approve')}
                                disabled={loading}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 flex items-center gap-1"
                            >
                                <CheckCircle className="w-4 h-4" />
                                Approve
                            </button>
                            <button
                                onClick={() => handleSubmit('reject')}
                                disabled={loading}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center gap-1"
                            >
                                <XCircle className="w-4 h-4" />
                                Reject
                            </button>
                        </div>
                    </>
                )}

                {invoice.status === 'approved' && (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Method <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                placeholder="e.g., Bank Transfer, Cash, Credit Card..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Reference <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                placeholder="e.g., Transaction ID, Receipt Number..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add any additional notes..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows="3"
                            />
                        </div>
                        {fieldError && invoice.status === 'approved' && (
                            <p className="mb-3 text-sm text-red-600">{fieldError}</p>
                        )}
                        <div className="flex gap-2 justify-center pb-3">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleSubmit('paid')}
                                disabled={loading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-1"
                            >
                                <DollarSign className="w-4 h-4" />
                                Mark as Paid
                            </button>
                        </div>
                        
                    </>
                )}
                </div>
            </div>
        </div>
    );
};

export default StatusUpdateModal; 